import { observable, action, computed } from 'mobx';
import { setter } from 'mobx-decorators';
import userSharedFormsValidator from '../../services/validations/userSharedFormsValidator';
import passwordMatchValidator from '../../services/validations/passwordMatchValidator';
import commonValidators from '../../services/validations/commonValidators';
import translationsStore from '../translationsStore';
import { LdapDirectoryType } from '../../services/enums/ldap';

class LDAPValidatorStore {

    @observable form1Errors = [];
    @observable form2Errors = [];
    @observable form3Errors = [];

    @setter @observable serverSettingsFormValid = false;
    @setter @observable ldapSchemaFormValid = false;
    @setter @observable ldapSynchronizationFormValid = false;

    // flags

    // form 1
    @observable nameValid = false;
    @observable hostValid = false;
    @observable portValid = false;
    @observable usernameValid = false;
    @observable passwordValid = false;

    // form 2
    @observable directoryTypeValid = false;
    @observable mapLdapToValid = false;
    @observable baseDnValid = false;
    @observable additionalUserDnValid = false;
    @observable userObjectSchemaValid = false;
    @observable userObjectFilterValid = false;
    @observable usernameAttributeValid = false;
    @observable firstNameAttributeValid = false;
    @observable lastNameAttributeValid = false;
    @observable emailAttributeValid = false;

    // form 3
    @observable defaultRoleIdValid = false;
    @observable defaultTeamIdValid = false;
    @observable additionalGroupDnValid = false;
    @observable groupObjectClassValid = false;
    @observable groupObjectFilterValid = false;
    @observable groupNameAttributeValid = false;
    @observable groupMembersValid = false;
    @observable userMemberShipValid = false;

    // conditions
    @setter @observable synchronizationEnabled = false;
    @setter @observable advancedTeamAndRoleMappingEnabled = false;
    @setter @observable defaultTeamEnabled = false;

    // error messages

    // form 1
    @setter @observable serverNameValidationMessage;
    @setter @observable hostValidationMessage;
    @setter @observable portValidationMessage;
    @setter @observable usernameValidationMessage;
    @setter @observable passwordValidationMessage;

    // form 2
    @setter @observable directoryTypeValidationMessage;
    @setter @observable mapLdapToValidationMessage;
    @setter @observable baseDnValidationMessage;
    @setter @observable additionalUserDnValidationMessage;  
    @setter @observable userObjectSchemaValidationMessage;
    @setter @observable userObjectFilterValidationMessage;
    @setter @observable usernameAttributeValidationMessage;
    @setter @observable firstNameAttributeValidationMessage;
    @setter @observable lastNameAttributeValidationMessage;
    @setter @observable emailAttributeValidationMessage;

    // form 3
    @setter @observable defaultRoleIdValidationMessage;
    @setter @observable defaultTeamIdValidationMessage;
    @setter @observable additionalGroupDnValidationMessage;
    @setter @observable groupObjectClassValidationMessage;
    @setter @observable groupObjectFilterValidationMessage;
    @setter @observable groupNameAttributeValidationMessage;
    @setter @observable groupMembersValidationMessage;
    @setter @observable userMemberShipValidationMessage;

    // form 1
    
    @action validateServerName(name) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(name);

        this.serverNameValidationMessage = validation.errors.first('field');

        this.form1Errors.push(validation.errorCount);

        this.nameValid = validation.errorCount == 0 ? true : false;
    }

    @action validateHost(host) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(host);

        this.hostValidationMessage = validation.errors.first('field');

        this.form1Errors.push(validation.errorCount);

        this.hostValid = validation.errorCount == 0 ? true : false;
    }

    @action validatePort(port) {

        const validation = commonValidators.validatePort(port);

        this.portValidationMessage = validation.errors.first('port');

        this.form1Errors.push(validation.errorCount);

        this.portValid = validation.errorCount == 0 ? true : false;
    }

    @action validateUserName(username) {

        const validation = userSharedFormsValidator.validateLDAPUserName(username);

        this.usernameValidationMessage = validation.errors.first('userName');

        this.form1Errors.push(validation.errorCount);

        this.usernameValid = validation.errorCount == 0 ? true : false;
    }

    @action validatePassword(password) {

        const validation = passwordMatchValidator.validatePasswordBase(password);

        this.passwordValidationMessage = validation.passwordValidationMessage;

        this.form1Errors.push(validation.validation.errorCount);

        this.passwordValid = validation.errorCount == 0 || validation.errorCount == undefined ? true : false;
    }

    @action validateServerSettingsForm(values) { 

        this.form1Errors = [];
        
        this.validateServerName(values.name);
        this.validateHost(values.host);
        this.validatePort(values.port);
        this.validateUserName(values.username);
        this.validatePassword(values.password);
        
        return this.runServerSettingsFormValidation();
    }

    // form 2
    @action validateDirectoryType(ldapDirectoryType) {

        if (ldapDirectoryType == 'none') {
            
            this.directoryTypeValidationMessage = translationsStore.translate('GENERIC_REQUIRED_FILED_ERROR_MESSAGE');

            this.form2Errors.push(1);

            this.directoryTypeValid = false;
        }
        else {

            this.directoryTypeValidationMessage = '';
            this.directoryTypeValid = true;
        }

        if ((ldapDirectoryType == LdapDirectoryType.activeDirectory && this.ssoEnabled == false)
                || ldapDirectoryType == LdapDirectoryType.customLDAPServer
                || ldapDirectoryType == LdapDirectoryType.openLDAP) {

            this.mapLdapToValid = true;
        }
    }

    @action validateMapLdapTo(ssoEnabled, mapLdapTo) {

        if (ssoEnabled == true && mapLdapTo == -1) {
                        
            this.mapLdapToValidationMessage = translationsStore.translate('GENERIC_REQUIRED_FILED_ERROR_MESSAGE');

            this.form2Errors.push(1);

            this.mapLdapToValid = false;
        }
        else {
            this.mapLdapToValidationMessage = '';
            this.mapLdapToValid = true;
        }
    }    
    
    @action validateBaseDn(baseDn) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(baseDn);

        this.baseDnValidationMessage = validation.errors.first('field');

        this.form2Errors.push(validation.errorCount);

        this.baseDnValid = validation.errorCount == 0 ? true : false;
    }

    @action validateUserObjectSchema(userObjectClass) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(userObjectClass);

        this.userObjectSchemaValidationMessage = validation.errors.first('field');

        this.form2Errors.push(validation.errorCount);

        this.userObjectSchemaValid = validation.errorCount == 0 ? true : false;
    }

    @action validateUserObjectFilter(userObjectFilter) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(userObjectFilter);

        this.userObjectFilterValidationMessage = validation.errors.first('field');

        this.form2Errors.push(validation.errorCount);

        this.userObjectFilterValid = validation.errorCount == 0 ? true : false;
    }

    @action validateUsernameAttribute(usernameAttribute) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(usernameAttribute);

        this.usernameAttributeValidationMessage = validation.errors.first('field');

        this.form2Errors.push(validation.errorCount);

        this.usernameAttributeValid = validation.errorCount == 0 ? true : false;
    }

    @action validateLDAPSchemaForm(values) { 

        this.form2Errors = [];
        
        this.validateDirectoryType(values.ldapDirectoryType);
        this.validateMapLdapTo(values.ssoEnabled, values.mapLdapTo);
        this.validateBaseDn(values.baseDn);
        this.validateUserObjectSchema(values.userObjectClass);
        this.validateUserObjectFilter(values.userObjectFilter);
        this.validateUsernameAttribute(values.usernameAttribute);
               
        return this.runLDAPSchemaFormValidation();
    }

    @action validateDefaultRoleId(roleId) {

        const validation = commonValidators.validateInteger(roleId);

        this.defaultRoleIdValidationMessage = validation.errors.first('number');

        this.form3Errors.push(validation.errorCount);

        this.defaultRoleIdValid = validation.errorCount == 0 ? true : false;
    }

    @action validateDefaultTeamId(teamId) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(teamId);

        this.defaultTeamIdValidationMessage = validation.errors.first('field');

        this.form3Errors.push(validation.errorCount);

        this.defaultTeamIdValid = validation.errorCount == 0 ? true : false;
    }

    @action validateGroupObjectClass(str) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(str);

        this.groupObjectClassValidationMessage = validation.errors.first('field');

        this.form3Errors.push(validation.errorCount);

        this.groupObjectClassValid = validation.errorCount == 0 ? true : false;
    }

    @action validateGroupObjectFilter(str) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(str);

        this.groupObjectFilterValidationMessage = validation.errors.first('field');

        this.form3Errors.push(validation.errorCount);

        this.groupObjectFilterValid = validation.errorCount == 0 ? true : false;
    }
    
    @action validateGroupNameAttribute(str) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(str);

        this.groupNameAttributeValidationMessage = validation.errors.first('field');

        this.form3Errors.push(validation.errorCount);

        this.groupNameAttributeValid = validation.errorCount == 0 ? true : false;
    }

    @action validateGroupMembersAttribute(str) {

        const validation = userSharedFormsValidator.validateGenericRequiredField(str);

        this.groupMembersValidationMessage = validation.errors.first('field');

        this.form3Errors.push(validation.errorCount);

        this.groupMembersValid = validation.errorCount == 0 ? true : false;
    }

    // forms validation

    @action validateLDAPSynchronizationForm(values) {

        this.form3Errors = [];

        if(this.synchronizationEnabled === true) {

            if(this.advancedTeamAndRoleMappingEnabled === false || 
                (this.advancedTeamAndRoleMappingEnabled === true && this.defaultTeamEnabled)){
                this.validateDefaultTeamId(values.defaultTeamId); 
            }

            if (this.advancedTeamAndRoleMappingEnabled === true) {
                this.validateGroupObjectClass(values.groupObjectClass);
                this.validateGroupObjectFilter(values.groupObjectFilter);
                this.validateGroupNameAttribute(values.groupNameAttribute);
                this.validateGroupMembersAttribute(values.groupMembersAttribute);
            }
        }

        return this.runLDAPSynchronizationFormValidation();
    }

    @action runServerSettingsFormValidation() {

        this.serverSettingsFormValid = true;

        if (this.form1Errors.filter((valid) => valid > 0).length > 0) {

            this.serverSettingsFormValid = false;
        }

        return this.serverSettingsFormValid;
    }

    @action runLDAPSchemaFormValidation() {

        this.ldapSchemaFormValid = true;

        if (this.form2Errors.filter((valid) => valid > 0).length > 0) {

            this.ldapSchemaFormValid = false;
        }

        return this.ldapSchemaFormValid;
    }

    @action runLDAPSynchronizationFormValidation() {

        this.ldapSynchronizationFormValid = true;

        if (this.form3Errors.filter((valid) => valid > 0).length > 0) {

            this.ldapSynchronizationFormValid = false;
        }

        return this.ldapSynchronizationFormValid;
    }

    @action resetForm() {

        this.form1Errors = [];
        this.form2Errors = [];
        this.form3Errors = [];

        this.nameValid = false;
        this.hostValid = false;
        this.portValid = false;
        this.usernameValid = false;
        this.passwordValid = false;

        // form 2
        this.directoryTypeValid = false;
        this.mapLdapToValid = false;
        this.baseDnValid = false;
        this.additionalUserDnValid = false;
        this.userObjectSchemaValid = false;
        this.userObjectFilterValid = false;
        this.usernameAttributeValid = false;
        this.firstNameAttributeValid = false;
        this.lastNameAttributeValid = false;
        this.emailAttributeValid = false;

        // form 3
        this.defaultRoleIdValid = false;
        this.defaultTeamIdValid = false;
        this.additionalGroupDnValid = false;
        this.groupObjectClassValid = false;
        this.groupObjectFilterValid = false;
        this.groupNameAttributeValid = false;
        this.groupMembersValid = false;
        this.userMemberShipValid = false;

        // form 1
        this.serverNameValidationMessage = '';
        this.hostValidationMessage = '';
        this.portValidationMessage = '';
        this.usernameValidationMessage = '';
        this.passwordValidationMessage = '';

        // form 2
        this.directoryTypeValidationMessage = '';
        this.mapLdapToValidationMessage = '';
        this.baseDnValidationMessage = '';
        this.additionalUserDnValidationMessage = '';      
        this.userObjectSchemaValidationMessage = '';
        this.userObjectFilterValidationMessage = '';
        this.usernameAttributeValidationMessage = '';
        this.firstNameAttributeValidationMessage = '';
        this.lastNameAttributeValidationMessage = '';
        this.emailAttributeValidationMessage = '';

        // form 3
        this.defaultRoleIdValidationMessage = '';
        this.defaultTeamIdValidationMessage = '';
        this.additionalGroupDnValidationMessage = '';
        this.groupObjectClassValidationMessage = '';
        this.groupObjectFilterValidationMessage = '';
        this.groupNameAttributeValidationMessage = '';
        this.groupMembersValidationMessage = '';
        this.userMemberShipValidationMessage = '';
    }

    @computed get isForm1Valid() {

        if (this.nameValid == true
            && this.hostValid == true
            && this.portValid == true
            && this.usernameValid == true
            && this.passwordValid == true) {
         
                return true;
        }
        
        return false;
    }

    @computed get isForm2Valid() {

        if (this.directoryTypeValid == true &&
            this.mapLdapToValid == true &&
            this.baseDnValid == true &&
            this.additionalUserDnValid == true &&
            this.userObjectSchemaValid == true &&
            this.userObjectFilterValid == true &&
            this.usernameAttributeValid == true &&
            this.firstNameAttributeValid == true &&
            this.lastNameAttributeValid == true &&
            this.emailAttributeValid == true) {
        
                return true;
        }
        
        return false;
    }

    @computed get isForm3Valid() {

        if (this.defaultRoleIdValid == true &&
            this.defaultTeamIdValid == true &&
            this.additionalGroupDnValid == true &&
            this.groupObjectClassValid == true &&
            this.groupObjectFilterValid == true &&
            this.groupNameAttributeValid == true &&
            this.groupMembersValid == true &&
            this.userMemberShipValid == true) {

            return true;
        }

        return false;
    }
}

export default LDAPValidatorStore;



// WEBPACK FOOTER //
// ./src/stores/ldap/ldapValidtor.js